import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, ArrowRight, Sprout, Sun, Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { markPlantReadyNow, isTenderPlant, getLastFrostDate } from '@/lib/growthAnchoring';
import { addDays, max as dateMax } from 'date-fns';

interface TaskDetailDialogProps {
  task: {
    id: string;
    task_type: string;
    title: string;
    description: string | null;
    scheduled_date: string;
    estimated_minutes: number | null;
    completed: boolean | null;
    user_plant_id: string;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskUpdated: () => void;
}

export function TaskDetailDialog({ task, open, onOpenChange, onTaskUpdated }: TaskDetailDialogProps) {
  const [newDate, setNewDate] = useState<Date | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [switchingSow, setSwitchingSow] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);

  if (!task) return null;

  const currentDate = parseISO(task.scheduled_date);
  const isSowingTask = task.task_type === 'seed_sowing';
  const titleLower = task.title.toLowerCase();
  const descLower = (task.description || '').toLowerCase();

  // Outdoor if title explicitly says "direct" or "outdoor"
  const isOutdoorSow = isSowingTask && (
    titleLower.includes('sow direct') ||
    titleLower.includes('outdoor') ||
    titleLower.includes('outside')
  );
  // Indoor if explicitly stated, OR if it's a sowing task without outdoor markers
  // (default to indoor since most seed_sowing stages originate as indoor)
  const isIndoorSow = isSowingTask && !isOutdoorSow;
  // Always show toggle for sowing tasks
  const canToggleSowMethod = isSowingTask;

  // Growth-driven "Ready now" — let the gardener pull harden_off / plant_out forward
  // when their plant is clearly ready, regardless of the calendar date.
  const isReadyTriggerTask = task.task_type === 'harden_off' || task.task_type === 'plant_out';
  const plantName = task.title.split(':')[0]?.trim() || '';
  const readyLabel = task.task_type === 'harden_off' ? 'Ready to harden off now' : 'Ready to plant out now';
  const readySubtext = task.task_type === 'harden_off'
    ? 'Pull this forward to today — also schedules planting out a week later.'
    : 'Pull this forward to today (skipped if frost risk for tender crops).';

  const handleReschedule = async () => {
    if (!newDate) return;
    setSaving(true);
    try {
      const dateStr = format(newDate, 'yyyy-MM-dd');
      await supabase.from('garden_tasks')
        .update({ scheduled_date: dateStr } as any)
        .eq('id', task.id);
      toast.success(`Moved to ${format(newDate, 'd MMM')}`);
      setNewDate(undefined);
      onTaskUpdated();
      onOpenChange(false);
    } catch {
      toast.error('Could not reschedule task');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReadyNow = async () => {
    if (!isReadyTriggerTask) return;
    setMarkingReady(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('growing_zone')
        .eq('user_id', user?.id || '')
        .maybeSingle();
      const zone = profile?.growing_zone || 'UK-Midlands';

      await markPlantReadyNow({
        userPlantId: task.user_plant_id,
        taskType: task.task_type as 'harden_off' | 'plant_out',
        plantName,
        zone,
      });

      toast.success(
        task.task_type === 'harden_off'
          ? `${plantName} is hardening off — planting out queued for next week 🌤️`
          : `${plantName} ready to plant out today 🌱`
      );
      onTaskUpdated();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || 'Could not update timeline');
    } finally {
      setMarkingReady(false);
    }
  };

  const handleToggleSowMethod = async () => {
    setSwitchingSow(true);
    try {
      const switchingToOutdoor = isIndoorSow;
      let newTitle = task.title;
      let newDescription = task.description || '';

      if (switchingToOutdoor) {
        // Switch to outdoor/direct
        newTitle = newTitle
          .replace(/Sow Seeds? Indoors?/i, 'Sow Direct')
          .replace(/Sow Indoors?/i, 'Sow Direct');
        // Fallback if title was just "Sow Seeds"
        if (newTitle === task.title && !newTitle.toLowerCase().includes('direct')) {
          newTitle = newTitle.replace(/Sow Seeds?/i, 'Sow Direct');
        }
        newDescription = 'Sow seeds directly outdoors when soil has warmed. Prepare soil well and keep moist.';
      } else {
        // Switch to indoor
        newTitle = newTitle
          .replace(/Sow Direct/i, 'Sow Seeds Indoors')
          .replace(/Sow Seeds$/i, 'Sow Seeds Indoors');
        newDescription = 'Sow seeds in small pots on a warm windowsill or heated propagator. Keep soil moist.';
      }

      // When switching to outdoor, push the sowing date forward by 4 weeks
      // (outdoor sowing is typically later than indoor)
      let newScheduledDate = task.scheduled_date;
      if (switchingToOutdoor) {
        const currentDate = parseISO(task.scheduled_date);
        const adjustedDate = new Date(currentDate);
        adjustedDate.setDate(adjustedDate.getDate() + 28); // +4 weeks
        newScheduledDate = format(adjustedDate, 'yyyy-MM-dd');
      } else {
        // Switching to indoor: pull the date back by 4 weeks if possible
        const currentDate = parseISO(task.scheduled_date);
        const adjustedDate = new Date(currentDate);
        adjustedDate.setDate(adjustedDate.getDate() - 28);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Don't schedule in the past
        if (adjustedDate >= today) {
          newScheduledDate = format(adjustedDate, 'yyyy-MM-dd');
        }
      }

      // Update the sowing task
      await supabase.from('garden_tasks')
        .update({ title: newTitle, description: newDescription, scheduled_date: newScheduledDate } as any)
        .eq('id', task.id);

      // When switching to outdoor, remove transplant-related tasks (they don't apply)
      if (switchingToOutdoor) {
        await supabase.from('garden_tasks')
          .delete()
          .eq('user_plant_id', task.user_plant_id)
          .in('task_type', ['harden_off', 'plant_out', 'first_transplant', 'second_transplant'] as any)
          .eq('completed', false);
      } else {
        // Switching to indoor: ensure harden_off + plant_out tasks exist (growth-anchored)
        // so the user can later tap "Ready to plant out now" on them.
        const { data: existing } = await supabase.from('garden_tasks')
          .select('task_type')
          .eq('user_plant_id', task.user_plant_id)
          .in('task_type', ['harden_off', 'plant_out'] as any);

        const existingTypes = new Set((existing || []).map((t: any) => t.task_type));
        const { data: { user } } = await supabase.auth.getUser();
        const { data: profile } = await supabase
          .from('profiles')
          .select('growing_zone')
          .eq('user_id', user?.id || '')
          .maybeSingle();
        const zone = profile?.growing_zone || 'UK-Midlands';

        // Anchor offsets to the (new) sowing date
        const sowDate = parseISO(newScheduledDate);
        const hardenOffDate = addDays(sowDate, 35);
        let plantOutDate = addDays(sowDate, 42);
        if (isTenderPlant(plantName)) {
          plantOutDate = dateMax([plantOutDate, getLastFrostDate(zone)]);
        }

        const toInsert: any[] = [];
        if (!existingTypes.has('harden_off') && user?.id) {
          toInsert.push({
            user_id: user.id,
            user_plant_id: task.user_plant_id,
            task_type: 'harden_off',
            title: `${plantName}: Harden Off`,
            description: 'Move outside for increasing periods over 7-10 days to acclimatise before planting out.',
            scheduled_date: format(hardenOffDate, 'yyyy-MM-dd'),
            estimated_minutes: 10,
            completed: false,
          });
        }
        if (!existingTypes.has('plant_out') && user?.id) {
          toInsert.push({
            user_id: user.id,
            user_plant_id: task.user_plant_id,
            task_type: 'plant_out',
            title: `${plantName}: Plant Out`,
            description: 'Plant outside in final position. Water in well and protect from slugs.',
            scheduled_date: format(plantOutDate, 'yyyy-MM-dd'),
            estimated_minutes: 25,
            completed: false,
          });
        }
        if (toInsert.length > 0) {
          await supabase.from('garden_tasks').insert(toInsert as any);
        }
      }

      toast.success(
        switchingToOutdoor
          ? 'Switched to outdoor sowing — transplant tasks removed 🌱'
          : 'Switched to indoor sowing — harden off & plant out scheduled 🪟'
      );
      onTaskUpdated();
      onOpenChange(false);
    } catch {
      toast.error('Could not update sowing method');
    } finally {
      setSwitchingSow(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">{task.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Description */}
          {task.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {task.description.replace(/\s*\((weekly|ongoing) until .+?\)$/, '')}
            </p>
          )}

          {/* Current date + reschedule */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Scheduled date
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-xl border border-border/60 px-3 py-2 text-sm text-foreground bg-muted/30">
                {format(currentDate, 'EEEE, d MMMM yyyy')}
              </div>
              {newDate && (
                <>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-primary">
                    {format(newDate, 'd MMM')}
                  </div>
                </>
              )}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full rounded-xl text-xs gap-2">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Move to a different day
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={newDate || currentDate}
                  onSelect={(d) => d && setNewDate(d)}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Sowing method toggle */}
          {canToggleSowMethod && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Sowing method
              </label>
              <div className="flex gap-2">
                <button
                  onClick={isIndoorSow ? undefined : handleToggleSowMethod}
                  disabled={switchingSow}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all",
                    isIndoorSow
                      ? "border-primary bg-primary/10 text-primary shadow-soft"
                      : "border-border/60 text-muted-foreground hover:border-primary/30 hover:shadow-soft cursor-pointer"
                  )}
                >
                  <Sprout className="h-4 w-4" />
                  Sow indoors
                </button>
                <button
                  onClick={isOutdoorSow ? undefined : handleToggleSowMethod}
                  disabled={switchingSow}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all",
                    isOutdoorSow
                      ? "border-primary bg-primary/10 text-primary shadow-soft"
                      : "border-border/60 text-muted-foreground hover:border-primary/30 hover:shadow-soft cursor-pointer"
                  )}
                >
                  <Sun className="h-4 w-4" />
                  Sow direct
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {isIndoorSow
                  ? 'Tap "Sow direct" to switch to outdoor sowing for this plant'
                  : 'Tap "Sow indoors" to start this plant on a windowsill instead'}
              </p>
            </div>
          )}

          {/* Growth-driven "Ready now" trigger for harden_off / plant_out */}
          {isReadyTriggerTask && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Plant looks ready?
              </label>
              <Button
                onClick={handleMarkReadyNow}
                disabled={markingReady}
                variant="outline"
                className="w-full rounded-xl border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:shadow-soft transition-all gap-2 h-11"
              >
                <Zap className="h-4 w-4" />
                {markingReady ? 'Updating timeline...' : readyLabel}
              </Button>
              <p className="text-[11px] text-muted-foreground">{readySubtext}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={() => { setNewDate(undefined); onOpenChange(false); }}
            >
              Cancel
            </Button>
            {newDate && (
              <Button
                className="flex-1 garden-gradient rounded-xl shadow-soft hover:shadow-medium transition-all"
                onClick={handleReschedule}
                disabled={saving}
              >
                {saving ? 'Moving...' : `Move to ${format(newDate, 'd MMM')}`}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
